import React, { useState } from "react";
import { Card, CardTitle, CardBody } from "reactstrap";

const WHATSAPP_SUPPORT_PANEL_STYLE = {
  border: "1px solid #D9E2EC",
  borderRadius: "8px",
  backgroundColor: "#FFFFFF",
  color: "#4A5568",
  padding: "24px",
  fontSize: "14px",
  lineHeight: 1.65,
};

const WHATSAPP_SECTION_TITLE_STYLE = {
  fontSize: "16px",
  fontWeight: "700",
  color: "#1F2937",
  marginTop: "18px",
  marginBottom: "10px",
};

const WHATSAPP_RULE_HEADING_STYLE = {
  fontWeight: "700",
  color: "#1F2937",
  marginBottom: "10px",
};

const WHATSAPP_BODY_TEXT_STYLE = {
  marginBottom: "8px",
};

const WHATSAPP_SECTION_STYLE = {
  marginTop: "18px",
};

const SUPPORT_DETAILS_STYLE = {
  marginBottom: "16px",
  padding: "12px 16px",
  borderRadius: "8px",
  backgroundColor: "#F8FAFC",
};

const SUPPORT_DETAIL_LABEL_STYLE = {
  display: "block",
  fontSize: "12px",
  fontWeight: "700",
  color: "#6B7280",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: "4px",
};

const SUPPORT_DETAIL_VALUE_STYLE = {
  marginBottom: "0",
  color: "#1F2937",
  fontWeight: "600",
  wordBreak: "break-word",
};

const SUPPORT_DETAIL_HEADER_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "4px",
};

const SUPPORT_DETAIL_TOGGLE_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#6B7280",
  cursor: "pointer",
  padding: "0",
  fontSize: "18px",
  lineHeight: 1,
};

const SUPPORT_WARNING_TEXT_STYLE = {
  marginTop: "8px",
  marginBottom: "0",
  color: "#B45309",
  fontSize: "12px",
  fontWeight: "600",
  lineHeight: 1.5,
};

const maskServicePin = (pin) => "•".repeat(String(pin).length);

const WhatsAppSupport = ({ supportId, servicePin }) => {
  const [isServicePinVisible, setIsServicePinVisible] = useState(false);

  return (
    <Card>
      <CardBody>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "2px",
            marginBottom: "16px",
          }}
        >
          <CardTitle tag="h3" style={{ marginBottom: "0" }}>WhatsApp Support</CardTitle>
          <span style={{ fontSize: "12px", color: "#6B7280", fontStyle: "italic", lineHeight: 1.4, marginTop: "5px" }}>
            Exclusive to SLA Business &amp; SLA Gold Members
          </span>
        </div>
        <div style={WHATSAPP_SUPPORT_PANEL_STYLE}>
          <p style={{ ...WHATSAPP_BODY_TEXT_STYLE, marginBottom: "12px" }}>
            Welcome to your premium WhatsApp support!
          </p>

          {(supportId || servicePin) && (
            <div style={SUPPORT_DETAILS_STYLE}>
              {supportId && (
                <div style={{ marginBottom: servicePin ? "12px" : "0" }}>
                  <span style={SUPPORT_DETAIL_LABEL_STYLE}>Support ID</span>
                  <p style={SUPPORT_DETAIL_VALUE_STYLE}>{supportId}</p>
                </div>
              )}
              {servicePin && (
                <div>
                  <span style={SUPPORT_DETAIL_LABEL_STYLE}>Service Pin</span>
                  <div style={SUPPORT_DETAIL_HEADER_STYLE}>
                    <p style={SUPPORT_DETAIL_VALUE_STYLE}>
                      {isServicePinVisible ? servicePin : maskServicePin(servicePin)}
                    </p>
                    <span
                      style={SUPPORT_DETAIL_TOGGLE_STYLE}
                      onClick={() => setIsServicePinVisible((currentValue) => !currentValue)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setIsServicePinVisible((currentValue) => !currentValue);
                        }
                      }}
                      aria-label={isServicePinVisible ? "Hide service pin" : "Show service pin"}
                      title={isServicePinVisible ? "Hide service pin" : "Show service pin"}
                      role="button"
                      tabIndex={0}
                    >
                      <i
                        className={`fa ${isServicePinVisible ? "fa-eye-slash" : "fa-eye"}`}
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                  <p style={SUPPORT_WARNING_TEXT_STYLE}>
                    <span role="img" aria-label="warning">⚠️</span>{" "}
                    Don&apos;t share this PIN with anyone. Incase the PIN is exposed, please report it to us immediately.
                  </p>
                </div>
              )}
            </div>
          )}

          <p style={{ marginBottom: "0" }}>
            To ensure your requests are handled securely and instantly, please review and
            strictly follow these mandatory ground rules.
          </p>

          <p style={WHATSAPP_SECTION_TITLE_STYLE}>
            <span role="img" aria-label="mobile phone">📱</span>{" "}
            Ground Rules
          </p>

          <div style={WHATSAPP_SECTION_STYLE}>
            <p style={WHATSAPP_RULE_HEADING_STYLE}>
              <span role="img" aria-label="lock">🔒</span>{" "}
              Rule 1: Use Your Verified Number Only
            </p>
            <p style={WHATSAPP_BODY_TEXT_STYLE}>
              You can only contact our support team using the specific mobile number registered and verified in your app profile.
            </p>
            <p style={WHATSAPP_BODY_TEXT_STYLE}>
              <strong>Keep it updated:</strong> Please ensure your current mobile number is saved in your profile settings so our system recognizes you.
            </p>
            <p style={{ marginBottom: "0" }}>
              <strong><span role="img" aria-label="warning">⚠️</span> Critical Note:</strong> Our system automatically filters incoming chats based on your profile data. If you message us from an unmatched or unverified number, we will <strong>NOT</strong> see your message, and our team will be unable to respond.
            </p>
          </div>

          <div style={WHATSAPP_SECTION_STYLE}>
            <p style={WHATSAPP_RULE_HEADING_STYLE}>
              <span role="img" aria-label="prohibited">🚫</span>{" "}
              Rule 2: Strictly No Sharing or adding in the Groups
            </p>
            <p style={WHATSAPP_BODY_TEXT_STYLE}>
              This dedicated WhatsApp support line is a premium, exclusive benefit just for you.
            </p>
            <p style={{ marginBottom: "0" }}>
              You are not allowed to share this WhatsApp number with any third party, colleague, or external user under any circumstances.
            </p>
          </div>

          <div style={WHATSAPP_SECTION_STYLE}>
            <p style={WHATSAPP_RULE_HEADING_STYLE}>
              <span role="img" aria-label="warning">⚠️</span>{" "}
              Policy Enforcement
            </p>
            <p style={WHATSAPP_BODY_TEXT_STYLE}>
              We take the security and integrity of our premium support channels very seriously.
              Any customer who fails to respect these basic rules will have their WhatsApp
              support privileges permanently revoked without any refund.
            </p>
            <p style={{ marginBottom: "0" }}>
              Thank you for helping us keep your support channel secure, fast, and efficient!
            </p>
          </div>

          <div
            onClick={() => {
              // Demo build: keep CTA visual only — do not open a real WhatsApp number.
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              cursor: "default",
              marginTop: "24px",
              opacity: 0.85,
            }}
            role="button"
            tabIndex={0}
            aria-label="WhatsApp support (demo preview)"
            title="WhatsApp support is preview-only in the demo"
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #2FBB62",
                boxSizing: "border-box",
                borderRadius: "8px",
                minHeight: "36px",
                padding: "9px 15px",
                backgroundColor: "#1F9D55",
                color: "#FFFFFF",
                fontWeight: "600",
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: "18px",
                  height: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  style={{ fill: "currentColor", display: "block", width: "18px", height: "18px" }}
                  viewBox="0 0 17 17"
                  aria-hidden="true"
                >
                  <path d="M16.5333 8.7668C16.5333 13.0668 13.02 16.5535 8.67999 16.5535C7.29999 16.5535 6.01333 16.2001 4.87999 15.5868L0.533325 16.9668L1.95333 12.7868C1.21185 11.5746 0.81965 10.1811 0.819992 8.76013C0.826659 4.45346 4.34666 0.966797 8.68666 0.966797C13.0133 0.966797 16.5333 4.45346 16.5333 8.7668ZM8.67999 2.20013C5.03999 2.20013 2.07999 5.14013 2.07999 8.75346C2.07999 10.1868 2.54666 11.5135 3.33333 12.5935L2.50666 15.0335L5.03999 14.2335C6.12135 14.9422 7.38707 15.3178 8.67999 15.3135C12.32 15.3135 15.28 12.3801 15.28 8.76013C15.2712 7.01671 14.5715 5.3479 13.3343 4.11948C12.0972 2.89106 10.4234 2.20325 8.67999 2.2068V2.20013ZM12.6467 10.5535C12.5933 10.4735 12.4667 10.4268 12.2733 10.3335C12.0867 10.2401 11.14 9.77346 10.96 9.71346C10.7867 9.6468 10.6533 9.61346 10.5267 9.8068C10.3933 10.0001 10.0267 10.4268 9.91999 10.5535C9.80666 10.6868 9.69333 10.7001 9.49999 10.6068C9.30666 10.5068 8.68666 10.3068 7.95333 9.65346C7.53454 9.26854 7.17509 8.82372 6.88666 8.33346C6.76666 8.14013 6.86666 8.04013 6.96666 7.9468C7.05333 7.86013 7.15999 7.72013 7.25333 7.61346C7.35333 7.50013 7.38666 7.41346 7.44666 7.29346C7.51333 7.16013 7.47999 7.05346 7.43333 6.96013C7.37999 6.86013 6.99999 5.92013 6.83333 5.54013C6.67333 5.15346 6.51333 5.22013 6.40666 5.22013C6.29333 5.22013 6.15999 5.20013 6.03333 5.20013C5.89999 5.20013 5.69999 5.25346 5.51999 5.44013C5.34666 5.63346 4.85333 6.09346 4.85333 7.04013C4.85333 7.97346 5.53999 8.88013 5.63333 9.01346C5.72666 9.14013 6.96666 11.1268 8.91999 11.8935C10.88 12.6601 10.88 12.4068 11.2333 12.3735C11.5867 12.3401 12.3667 11.9068 12.5333 11.4668C12.6933 11.0201 12.6933 10.6335 12.6467 10.5535Z"></path>
                </svg>
              </div>
              <div style={{ wordBreak: "break-word", fontSize: "14px", marginLeft: "7px" }}>
                Message us on WhatsApp
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default WhatsAppSupport;
